import React from 'react';
import env from '../../env';

/**
 * WebsiteSchema Component
 * 
 * Implements Schema.org WebSite structured data for SEO.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/WebSite
 * 
 * This schema should be used on the homepage to help search engines
 * understand the website structure and enable site search functionality.
 * 
 * @param {Object} props
 * @param {Object} props.organization - Optional organization reference
 * @param {string} props.searchActionUrl - Optional search action URL for site search
 * 
 * @example
 * <WebsiteSchema />
 * <WebsiteSchema searchActionUrl="/search?q={search_term_string}" />
 */
export function WebsiteSchema({ searchActionUrl }) {
  React.useEffect(() => {
    // Remove any existing website schema script
    const existingScript = document.querySelector('script[data-website-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create new JSON-LD script for website
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-website-schema', 'true');
    
    // Build Website schema according to Schema.org specification
    const websiteSchema = {
      "@context": "https://schema.org/",
      "@type": "WebSite",
      "name": env.siteName,
      "url": env.baseOrigin,
      "description": "Scunci - Premium hair accessories and styling tools for all hair types. Shop our collection of ponytail holders, hair ties, headbands, and professional styling products.",
      "publisher": {
        "@type": "Organization",
        "name": env.siteName,
        "url": env.baseOrigin,
        "logo": {
          "@type": "ImageObject",
          "url": env.baseOrigin + "/logo192.png"
        }
      },
      "inLanguage": "en-AU",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": searchActionUrl || `${env.baseOrigin}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      },
      "sameAs": [
        "https://www.facebook.com/ScunciAustralia",
        "https://www.instagram.com/scunci_au"
      ]
    };

    script.textContent = JSON.stringify(websiteSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-website-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [searchActionUrl]); // Re-run if searchActionUrl changes

  // Return null since we're using direct DOM manipulation
  return null;
}
